import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import * as attributesApi from "./api";

export const useProductTypesStore = create(
  immer((set, get) => ({
    // State
    attributes: [],
    currentProductType: null,
    loading: false,
    error: null,

    // Actions
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),

    // Fetch attributes for a specific hierarchy
    fetchAttributes: async (marketplaceId, hierarchyCode = null) => {
      set({ loading: true, error: null });
      try {
        const attributes = await attributesApi.getAttributes(
          marketplaceId,
          hierarchyCode ?? ""
        );
        set({ attributes, loading: false });
        return attributes;
      } catch (error) {
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    // Get single attribute
    fetchAttribute: async (marketplaceId, attributeId) => {
      set({ loading: true, error: null });
      try {
        const attribute = await attributesApi.getAttribute(
          marketplaceId,
          attributeId
        );
        set({ loading: false });
        return attribute;
      } catch (error) {
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    // Create new attribute
    createAttribute: async (marketplaceId, attributeData) => {
      set({ loading: true, error: null });
      try {
        const newAttribute = await attributesApi.createAttribute(
          marketplaceId,
          attributeData
        );
        set((state) => {
          state.attributes.push(newAttribute);
          state.loading = false;
        });
        return newAttribute;
      } catch (error) {
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    // Update existing attribute
    updateAttribute: async (marketplaceId, attributeId, attributeData) => {
      set({ loading: true, error: null });
      try {
        const updatedAttribute = await attributesApi.updateAttribute(
          marketplaceId,
          attributeId,
          attributeData
        );
        set((state) => {
          const index = state.attributes.findIndex(
            (attr) => attr.id === attributeId
          );
          if (index !== -1) {
            state.attributes[index] = updatedAttribute;
          }
          state.loading = false;
        });
        return updatedAttribute;
      } catch (error) {
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    // Delete attribute
    deleteAttribute: async (marketplaceId, attributeId) => {
      set({ loading: true, error: null });
      try {
        await attributesApi.deleteAttribute(marketplaceId, attributeId);
        set((state) => {
          state.attributes = state.attributes.filter(
            (attr) => attr.id !== attributeId
          );
          state.loading = false;
        });
      } catch (error) {
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    // Set current product type and its attributes from the hydrated data
    setCurrentProductType: (productType) => {
      set((state) => {
        state.currentProductType = productType;
        // Extract attributes from hydratedType if available
        if (productType?.hydratedType?.attributes) {
          state.attributes = productType.hydratedType.attributes;
        } else {
          // Clear attributes if no hydratedType is available
          state.attributes = [];
        }
      });
    },

    // Set attributes directly (for backward compatibility or manual updates)
    setAttributes: (attributes) => set({ attributes }),

    // Clear all data
    clearData: () =>
      set({
        attributes: [],
        currentProductType: null,
        error: null,
        loading: false,
      }),
  }))
);
